import React, { useEffect, useMemo, useState } from 'react';
import { cities } from '../data/cities';
import { metroContentNotes } from '../data/contentNotes';
import { sports } from '../data/sports';
import type { CitySlug, SportSlug } from '../types/landing';
import { allVenuesIn, sportsOfferedIn } from '../utils/inventory';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ContentNote } from '../components/ContentNote';
import { ExploreMore } from '../components/ExploreMore';
import { FinalCta } from '../components/FinalCta';
import { VenueMap } from '../components/FindNearYou/VenueMap';
import { MetroIntro } from '../components/metro/MetroIntro';
import { MetroProgramDirectory } from '../components/metro/MetroProgramDirectory';
import { MobileStickyCta } from '../components/MobileStickyCta';
import { NotesLegend } from '../components/NotesLegend';
import { Reviews } from '../components/Reviews';
import { SiteFooter } from '../components/SiteFooter';
import { SiteHeader } from '../components/SiteHeader';
import { SportHero } from '../components/SportHero';
interface MetroLandingProps {
  citySlug: CitySlug;
  onSelectSport: (slug: SportSlug) => void;
}
const METRO_HERO = 'https://cdn.magicpatterns.com/uploads/u53bE9GBBKJXw8cksJRNy1/10_12_24___i9_Marketing0373.jpg';
function note(section: string) {
  return metroContentNotes.find(n => n.section === section) ?? metroContentNotes[0];
}
export function MetroLanding({
  citySlug,
  onSelectSport
}: MetroLandingProps) {
  const city = cities.find(c => c.slug === citySlug) ?? cities[0];
  const venues = useMemo(() => allVenuesIn(city), [city]);
  const offered = useMemo(() => {
    const slugs = sportsOfferedIn(city);
    return sports.filter(s => slugs.includes(s.slug));
  }, [city]);
  const sportList = offered.map(s => s.name).join(', ').replace(/, ([^,]*)$/, ' and $1');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    document.title = `${city.name} Youth Sports Leagues | i9 Sports`;
  }, [city]);
  const scrollToMap = () => document.getElementById('metro-map')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
  return <div className="min-h-screen w-full" data-id="element-273" data-mp="29q7c89i8e3a6">
      <SiteHeader data-id="element-274" data-mp="2nnvwpg8dz8ep" />
      <main className="mx-auto w-full max-w-6xl" data-id="element-275" data-mp="2nof3q1wi0cdy">
        <div className="relative px-4 md:px-0" data-id="element-276" data-mp="2ioj2ubj3l12m">
          <Breadcrumbs showNote={false} items={[{
          label: 'Home',
          href: '/'
        }, {
          label: `${city.name} Youth Sports`
        }]} data-id="element-277" data-mp="2inrcbf4cxtj7" />
          <ContentNote kind="dynamic" detail={note('Breadcrumbs').detail} className="right-0 top-1 hidden md:block" data-id="element-278" data-mp="2wm7kzbls5u3z" />
        </div>
        <div className="overflow-hidden bg-white shadow-[0_0_0_1px_#E3E7EE]" data-id="element-279" data-mp="36du5jyk0d5je">
          <div className="relative" data-id="element-280" data-mp="36fy749ufpsgi">
            <SportHero title={`${city.name} Youth Sports Leagues`} intro={`Fun, organized youth sports for kids ages 3 and up at convenient locations across the ${city.name} area.`} image={METRO_HERO} imageAlt={`Kids playing i9 Sports in ${city.name}`} ctaLabel="Find A Program" onCta={scrollToMap} showNote={false} data-id="element-281" data-mp="31eqxs6cn8l7q" />
            <ContentNote kind="wp" detail={note('Hero').detail} data-id="element-282" data-mp="2unwkiogg4oj" />
          </div>
          <div className="relative" data-id="element-283" data-mp="3kzr6cgpd94va">
            <MetroIntro cityName={city.name} sportList={sportList} image={sports[2].heroImage} onCta={scrollToMap} data-id="element-284" data-mp="3l2wvtx1wq8rj" />
            <ContentNote kind="wp" detail={note('Intro & what to expect').detail} data-id="element-285" data-mp="3q1q7rl1jprpl" />
          </div>
          <div className="relative border-t border-brand-line" data-id="element-286" data-mp="3b96ct7ojuqj8">
            <ExploreMore heading={`Youth sports in ${city.name} by sport`} cityName={city.name} citySlug={city.slug} sports={offered} onSelectSport={onSelectSport} showNote={false} data-id="element-287" data-mp="3g3ag9gylwgfk" />
            <ContentNote kind="hybrid" detail={note('Youth sports by sport').detail} data-id="element-288" data-mp="2sd6gxlsgb2ud" />
          </div>
          <div id="metro-map" className="relative h-[380px] scroll-mt-4 border-y border-brand-line md:h-[460px]" data-id="element-289" data-mp="2xa6ok14y8dnl">
            <VenueMap venues={venues} center={city.center} selectedId={selectedId} onSelect={setSelectedId} visibleKey="metro" data-id="element-290" data-mp="4g83mj5cxlaf" />
            <ContentNote kind="dynamic" detail={note('Map').detail} className="right-3 top-3 z-[500]" data-id="element-291" data-mp="3vpewdlvmgehr" />
          </div>
          <div className="relative" data-id="element-292" data-mp="3lxiudzqf0489">
            <MetroProgramDirectory city={city} data-id="element-293" data-mp="3lwg51w8qq9ug" />
            <ContentNote kind="dynamic" detail={note('Browse all programs').detail} data-id="element-294" data-mp="3gy693jf6z57s" />
          </div>
          <div className="relative" data-id="element-295" data-mp="3cwkrjkrpq2r9">
            <FinalCta title="Find A Program Near You" citySlug={city.slug} browseLabel={`Or browse every location in ${city.name}`} onBrowse={scrollToMap} showNote={false} data-id="element-296" data-mp="3cvhyb66x3o86" />
            <ContentNote kind="hybrid" detail={note('Find a program CTA').detail} data-id="element-297" data-mp="1663du66qfz5e" />
          </div>
          <div className="relative" data-id="element-298" data-mp="1170khj047hd9">
            <Reviews citySlug={city.slug} showNote={false} data-id="element-299" data-mp="1181vxol1m5vi" />
            <ContentNote kind="dynamic" detail={note('Reviews').detail} data-id="element-300" data-mp="18qj9q6zyc6ua" />
          </div>
        </div>
      </main>
      <div className="mt-12" data-id="element-301" data-mp="1dpcp64ysu7x3">
        <SiteFooter data-id="element-302" data-mp="1doktyv946c7c" />
      </div>
      <MobileStickyCta label="Find A Program" onClick={scrollToMap} data-id="element-303" data-mp="1dnjfshwsx9le" />
      <NotesLegend notes={metroContentNotes} title="Metro page notes" subtitle="Parent page at /{city}" data-id="element-304" data-mp="1ihnlyyy8tdlf" />
    </div>;
}